import {
  "profile-editors.sleep_quality" as qualityLabel,
  "improve.sleep.why" as sleepWhy,
  "profile-editors.sleep_weekday" as weekdayLabel,
  "profile-editors.sleep_weekend" as weekendLabel,
} from "@repo/i18n";
import { useCallback } from "react";
import { Segmented, Stepper } from "../../../ui/controls";
import { type SleepQuality, sleepQualityOptions } from "./options";
import { FieldError, FieldLabel, InfoNote } from "./parts";

/** The contract's bounds on habitual sleep, in half hours (profile.ts). */
export const SLEEP_MIN_HOURS = 4;
export const SLEEP_MAX_HOURS = 10;
export const SLEEP_DEFAULT_HOURS = 7;
export const SLEEP_DEFAULT_QUALITY = 3;

export interface SleepDraft {
  /** 1 (poor) … 5 (good). */
  readonly quality: number;
  readonly weekday: number;
  readonly weekend: number;
}

export interface SleepFormProps {
  error?: string;
  onChange: (value: SleepDraft) => void;
  value: SleepDraft;
}

const HOURS: Intl.NumberFormatOptions = { style: "unit", unit: "hour" };

/**
 * Weekday and weekend hours on steppers, and the quality (A2 `SleepSheet`,
 * editors.tsx:727-782). A2 asked quality on three levels; the contract keeps
 * five, and a five-way segmented control still fits a phone.
 */
export function SleepForm({ value, onChange, error }: SleepFormProps) {
  const setWeekday = useCallback(
    (weekday: number) => onChange({ ...value, weekday }),
    [onChange, value]
  );
  const setWeekend = useCallback(
    (weekend: number) => onChange({ ...value, weekend }),
    [onChange, value]
  );
  const setQuality = useCallback(
    (quality: SleepQuality) => onChange({ ...value, quality: Number(quality) }),
    [onChange, value]
  );
  return (
    <div>
      <div className="overflow-hidden rounded-2xl bg-surface shadow">
        <div className="flex items-center justify-between gap-3 p-4">
          <span className="font-medium text-base">{weekdayLabel()}</span>
          <Stepper
            format={HOURS}
            label={weekdayLabel()}
            max={SLEEP_MAX_HOURS}
            min={SLEEP_MIN_HOURS}
            onValueChange={setWeekday}
            step={0.5}
            value={value.weekday}
          />
        </div>
        <div className="flex items-center justify-between gap-3 border-line border-t p-4">
          <span className="font-medium text-base">{weekendLabel()}</span>
          <Stepper
            format={HOURS}
            label={weekendLabel()}
            max={SLEEP_MAX_HOURS}
            min={SLEEP_MIN_HOURS}
            onValueChange={setWeekend}
            step={0.5}
            value={value.weekend}
          />
        </div>
      </div>
      <div className="mt-6">
        <FieldLabel>{qualityLabel()}</FieldLabel>
        <Segmented
          label={qualityLabel()}
          onValueChange={setQuality}
          options={sleepQualityOptions()}
          value={String(value.quality) as SleepQuality}
        />
      </div>
      {error ? <FieldError>{error}</FieldError> : null}
      <InfoNote>{sleepWhy()}</InfoNote>
    </div>
  );
}
